import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  FileText,
  Filter,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import type { AuditLogsFilters } from '@/lib/api'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuditLogs } from '@/lib/api'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/settings/audit-logs')({
  component: AuditLogsLayout,
})

function AuditLogsLayout() {
  const location = useLocation()

  // If we're on a child route (like /settings/audit-logs/123), render the outlet
  // Otherwise render the main audit logs page
  if (location.pathname !== '/settings/audit-logs') {
    return <Outlet />
  }

  return <AuditLogsPage />
}

// Helper function to get default filters (last 30 minutes)
const getDefaultFilters = (): AuditLogsFilters => {
  const now = new Date()
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000)

  return {
    limit: 50,
    offset: 0,
    start_time: thirtyMinutesAgo.toISOString(),
    end_time: now.toISOString(),
  }
}

function AuditLogsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<AuditLogsFilters>(getDefaultFilters())
  const [showFilters, setShowFilters] = useState(false)
  const [tempFilters, setTempFilters] = useState<AuditLogsFilters>(filters)

  const { data, isLoading, error, refetch, isFetching } = useAuditLogs(filters)

  const handleApplyFilters = () => {
    setFilters(tempFilters)
    setShowFilters(false)
  }

  const handleResetFilters = () => {
    const resetFilters = getDefaultFilters()
    setTempFilters(resetFilters)
    setFilters(resetFilters)
    setShowFilters(false)
  }

  const handleNextPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: (prev.offset || 0) + (prev.limit || 50),
    }))
  }

  const handlePrevPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset || 0) - (prev.limit || 50)),
    }))
  }

  const getStatusIcon = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
      return <CheckCircle className="h-4 w-4 text-green-500" />
    } else if (statusCode >= 400 && statusCode < 500) {
      return <AlertCircle className="h-4 w-4 text-orange-500" />
    } else if (statusCode >= 500) {
      return <XCircle className="h-4 w-4 text-red-500" />
    }
    return <AlertCircle className="h-4 w-4 text-muted-foreground" />
  }

  const getStatusColorClass = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
      return 'text-green-600 dark:text-green-400'
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'text-orange-600 dark:text-orange-400'
    } else if (statusCode >= 500) {
      return 'text-red-600 dark:text-red-400'
    }
    return 'text-muted-foreground'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return localizeDigits(
      date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    )
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) {
      return localizeDigits(`${ms.toFixed(0)}ms`)
    }
    return localizeDigits(`${(ms / 1000).toFixed(2)}s`)
  }

  // Helper to format ISO string to datetime-local input format (YYYY-MM-DDTHH:mm)
  const toDatetimeLocal = (isoString?: string) => {
    if (!isoString) return ''
    return isoString.slice(0, 16) // Remove seconds and timezone
  }

  // Helper to convert datetime-local input to ISO string
  const toISOString = (datetimeLocal: string) => {
    if (!datetimeLocal) return undefined
    return new Date(datetimeLocal).toISOString()
  }

  const currentPage =
    Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1
  const totalPages = data ? Math.ceil(data.total / (filters.limit || 50)) : 0
  const hasNextPage = data
    ? (filters.offset || 0) + (filters.limit || 50) < data.total
    : false
  const hasPrevPage = (filters.offset || 0) > 0

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">{t('auditLogs.title')}</h1>
                <p className="text-xs text-muted-foreground">
                  {t('auditLogs.description')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                disabled={isFetching}
                className="h-8 w-8"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                />
              </Button>
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                className="h-8 w-8"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Filters Card */}
          {showFilters && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('auditLogs.filters.title')}
                </CardTitle>
                <CardDescription>
                  {t('auditLogs.filters.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* User Account Filter */}
                <div className="space-y-2">
                  <Label htmlFor="user-account">
                    {t('auditLogs.filters.userAccount')}
                  </Label>
                  <Input
                    id="user-account"
                    placeholder={t('auditLogs.filters.userAccountPlaceholder')}
                    value={tempFilters.user_account || ''}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        user_account: e.target.value || undefined,
                      })
                    }
                  />
                </div>

                {/* Action Filter */}
                <div className="space-y-2">
                  <Label htmlFor="action">
                    {t('auditLogs.filters.action')}
                  </Label>
                  <Input
                    id="action"
                    placeholder={t('auditLogs.filters.actionPlaceholder')}
                    value={tempFilters.action || ''}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        action: e.target.value || undefined,
                      })
                    }
                  />
                </div>

                {/* Status Code Filter */}
                <div className="space-y-2">
                  <Label htmlFor="status-code">
                    {t('auditLogs.filters.statusCode')}
                  </Label>
                  <Select
                    value={tempFilters.status_code?.toString() || 'all'}
                    onValueChange={(value) =>
                      setTempFilters({
                        ...tempFilters,
                        status_code:
                          value === 'all' ? undefined : parseInt(value, 10),
                      })
                    }
                  >
                    <SelectTrigger id="status-code">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t('auditLogs.filters.allStatus')}
                      </SelectItem>
                      <SelectItem value="200">200 - OK</SelectItem>
                      <SelectItem value="201">201 - Created</SelectItem>
                      <SelectItem value="400">400 - Bad Request</SelectItem>
                      <SelectItem value="401">401 - Unauthorized</SelectItem>
                      <SelectItem value="403">403 - Forbidden</SelectItem>
                      <SelectItem value="404">404 - Not Found</SelectItem>
                      <SelectItem value="500">
                        500 - Internal Server Error
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Filters */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="start-time">
                      {t('auditLogs.filters.startTime')}
                    </Label>
                    <Input
                      id="start-time"
                      type="datetime-local"
                      value={toDatetimeLocal(tempFilters.start_time)}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          start_time: toISOString(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-time">
                      {t('auditLogs.filters.endTime')}
                    </Label>
                    <Input
                      id="end-time"
                      type="datetime-local"
                      value={toDatetimeLocal(tempFilters.end_time)}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          end_time: toISOString(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleApplyFilters}
                    className="flex-1"
                    size="sm"
                  >
                    {t('auditLogs.filters.apply')}
                  </Button>
                  <Button
                    onClick={handleResetFilters}
                    variant="outline"
                    className="flex-1"
                    size="sm"
                  >
                    {t('auditLogs.filters.reset')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <Card>
              <CardContent className="py-12">
                <LoadingSpinner label={t('auditLogs.loading')} />
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive">
                      {t('auditLogs.error')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {error instanceof Error
                        ? error.message
                        : t('auditLogs.errorUnknown')}
                    </p>
                  </div>
                  <Button onClick={() => refetch()} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 me-2" />
                    {t('auditLogs.retry')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results Info */}
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {data && !isLoading && (
            <Card className="border-dashed">
              <CardContent className="py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('auditLogs.showing')} {localizeDigits(data.logs.length)}{' '}
                    {t('auditLogs.of')} {localizeDigits(data.total)}{' '}
                    {t('auditLogs.logs')}
                  </span>
                  {totalPages > 1 && (
                    <span className="text-muted-foreground">
                      {t('auditLogs.page')} {localizeDigits(currentPage)}{' '}
                      {t('auditLogs.of')} {localizeDigits(totalPages)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Logs List */}
          {data && data.logs.length > 0 && (
            <div className="space-y-2">
              {data.logs.map((log) => (
                <Card
                  key={log.id}
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() =>
                    navigate({
                      to: '/settings/audit-logs/$logId',
                      params: { logId: log.id.toString() },
                    })
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {getStatusIcon(log.response_status)}
                        <div className="flex-1 min-w-0">
                          {/* Top row: action (method + path) and status */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">
                              {log.action}
                            </span>
                            <span
                              className={`font-mono text-xs font-semibold ${getStatusColorClass(log.response_status)}`}
                            >
                              {localizeDigits(log.response_status)}
                            </span>
                          </div>

                          {/* Middle row: user account */}
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span className="truncate">{log.user_account}</span>
                          </div>

                          {/* Bottom row: timestamp and duration */}
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>{formatDate(log.timestamp)}</span>
                            <span>•</span>
                            <span>{formatDuration(log.duration_ms ?? 0)}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State */}
          {data && data.logs.length === 0 && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t('auditLogs.noLogs')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('auditLogs.noLogsDescription')}
                    </p>
                  </div>
                  {(filters.user_account ||
                    filters.action ||
                    filters.status_code ||
                    filters.start_time ||
                    filters.end_time) && (
                    <Button
                      onClick={handleResetFilters}
                      variant="outline"
                      size="sm"
                    >
                      {t('auditLogs.filters.reset')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {data && data.logs.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <Button
                onClick={handlePrevPage}
                disabled={!hasPrevPage}
                variant="outline"
                size="sm"
              >
                {t('auditLogs.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('auditLogs.page')} {localizeDigits(currentPage)}{' '}
                {t('auditLogs.of')} {localizeDigits(totalPages)}
              </span>
              <Button
                onClick={handleNextPage}
                disabled={!hasNextPage}
                variant="outline"
                size="sm"
              >
                {t('auditLogs.next')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
